import React, { useEffect, useState, useRef, useCallback } from "react";
import { cqAPI, getBid, loadCustomers, Auth } from "../../lib/Auth";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Button,
  useTheme,
  useColorMode,
  IconButton,
  Image,
  useToast,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Link,
  useBreakpointValue,
  useColorModeValue,
  Spacer,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody, MenuButton, MenuItem, MenuList, Menu, ListItem, UnorderedList, NumberInput, NumberInputField,
  NumberInputStepper,
} from "@chakra-ui/react";
import EditableText from "../EditableText/EditableText";
import { useNavigate } from "react-router-dom";
import "./AllCustomers.css";
import {
  AddIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import debounce from "lodash.debounce";
import { components } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RiResetLeftFill } from "react-icons/ri";
import { faEllipsisV, faSearch } from "@fortawesome/free-solid-svg-icons";
import AsyncSelect from "react-select/async";

const libraries = ["places"];
const isPartner =
  window.location.hostname.includes("adec") ||
  window.location.hostname.includes("a-dec");
  
const SUBSCRIPTION_PART_OPTIONS = [
  '71.0041.00, Standard, Trial', '71.0042.00, Advanced, Trial', '71.0030.00, Standard, SUB', '71.0031.00, Advanced, SUB', '71.0032.00, Ultimate, SUB', '71.0033.00, Standard, Renewal', '71.0034.00, Advanced, Renewal', '71.0035.00, Ultimate, Renewal'
];

function AllCustomers() {
  const googleMap = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: process.env.REACT_APP_MAP_API,
    libraries,
  });
  const [customers, setCustomers] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [bundles, setBundles] = useState([]);
  const toast = useToast();
  const isAdecPartner = isPartner && Auth.user?.isSuperAdmin;
  const navigate = useNavigate();
  const autocompleteRef = useRef({});
  const {
    isOpen: isNewBusinessOpen,
    onOpen: onNewBusinessOpen,
    onClose,
  } = useDisclosure();
  const onNewBusinessClose = () => onClose();

  const theme = useTheme();
  const { colorMode } = useColorMode();
  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  // Fetch all customers
  const fetchAllCustomers = async () => {
    const bid = getBid();
    const response = bid
      ? await cqAPI.get(`/locations?parents_only=1`)
      : await cqAPI.get(`/locations?parents_only=1&bid=`);
    if (response?.type === "ok") {
      setCustomers(response.data);
    }
  };

  const updatePartnerData = async (bid, field, value) => {
    const res = await cqAPI.put('/a-dec/partner-data', { bid, [field]: value });
    if (res?.type === 'ok') {
      setCustomers(prev => prev.map(c => c.bid === bid ? { ...c, [field]: value } : c));
      return true;
    }
    return false;
  };

  const exportCustomers = async (format) => {
    try {
      const url = await cqAPI.downloadFile(`/a-dec/export?format=${format}&bid=`);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Export failed', status: 'error' });
    }
  };

  // Deleting business
  const [businessToDel, setBusinessToDel] = useState('');
  const [monthsToCharge, setMonthsToCharge] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    isOpen: isDeleteRequirementsOpen,
    onOpen: onDeleteRequirementsOpen,
    onClose: onDeleteRequirementsClose
  } = useDisclosure();
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose
  } = useDisclosure();
  const {
    isOpen: isStopDeletionModalOpen,
    onOpen: onStopDeletionModalOpen,
    onClose: onStopDeletionModalClose
  } = useDisclosure();
  const {
    isOpen: isContractCommitmentOpen,
    onOpen: onContractCommitmentOpen,
    onClose: onContractCommitmentClose
  } = useDisclosure();
  const {
    isOpen: isDelSuccessOpen,
    onOpen: onDelSuccessOpen,
    onClose: onDelSuccessClose
  } = useDisclosure();

  const checkDeletionReqs = async (bid) => {
    try {
      const response = await cqAPI.post('/settings/business/can-delete', {
        bid
      });
      if (response?.type === 'ok') {
        return response.data?.canDelete || false;
      }
      setIsLoading(false);
      return false;
    } catch (e) {
      console.error('Error checking deletion requirements:', e);
      return false;
    }
  };

  const deleteRequirementsModal = (
    <Modal isOpen={isDeleteRequirementsOpen} onClose={onDeleteRequirementsClose} size={'lg'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Business deletion requirements</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack mr={10}>
            <Text>
              To delete this business, please do the following first:
            </Text>
            <UnorderedList spacing={2} stylePosition="outside" pl={6}>
              <ListItem>Release all phone numbers after porting finished</ListItem>
            </UnorderedList>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button mr={3} onClick={onDeleteRequirementsClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const scheduleDelete = async () => {
    try {
      setIsLoading(true);
      const response = await cqAPI.post('/settings/business/schedule-delete', {
        bid: businessToDel.bid
      });
      setIsLoading(false);
      if (response?.type === 'ok') {
        onDeleteConfirmationModalClose();
        onDelSuccessOpen();
      } else {
        toast({
          title: "Couldn't schedule deletion",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true
        })
      }
    } catch (e) {
      console.error('Error scheduling business deletion:', e);
    }
  }

  const deleteConfirmationModal = (
    <Modal isOpen={isDeleteConfirmationModalOpen} onClose={onDeleteConfirmationModalClose} size={'lg'}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Delete Practice?</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack spacing={4} mr={10}>
            <Text>Permanently delete
              <Text as={"span"} fontWeight={"bold"}>{' '+businessToDel.name}</Text>
              {'\''}s data in 10 days?
              <Text fontStyle={"italic"}>Once deleted, this cannot be recovered!</Text>
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button mr={3} variant={'ghost'} onClick={onDeleteConfirmationModalClose} isDisabled={isLoading}>Cancel</Button>
          <Button mr={3} onClick={scheduleDelete} background="red.500" isDisabled={isLoading}>
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const cancelDeletion = async () => {
    try {
      setIsLoading(true);
      const response = await cqAPI.del('/settings/business/cancel-deletion', {
        bid: businessToDel.bid
      });
      setIsLoading(false);
      if (response?.type === 'ok') {
        toast({
          title: "Success",
          description: `${businessToDel.name}'s data deletion has been cancelled.`,
          status: "success"
        });
      }
    } catch (e) {
      console.error('Error cancelling deletion:', e);
    }
  };

  const stopDeletionModal = (
    <Modal isOpen={isStopDeletionModalOpen} onClose={onStopDeletionModalClose} size={'md'}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Stop Deletion?</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack spacing={4} mr={10}>
            <Text>Stop
              <Text as={"span"} fontWeight={"bold"}> {' ' + businessToDel.name}</Text>
              {'\''}s data from being deleted?
              <Text fontStyle={"italic"} alignSelf={"flex-start"}>You will need to set up Subscriptions again.</Text>
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button mr={3} variant={"ghost"} onClick={onStopDeletionModalClose} isDisabled={isLoading}>Cancel</Button>
          <Button onClick={() => {
            cancelDeletion();
            onStopDeletionModalClose();
            fetchAllCustomers();
          }} mr={3} isDisabled={isLoading}>Confirm</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  // For deleting non-partner businesses, we need to charge for remaining months first
  const chargeCustomerForRemainingMonths = async () => {
    try {
      setIsLoading(true);
      const response = await cqAPI.post('/settings/business/charge-remaining-contract', {
        bid: businessToDel.bid,
        months: monthsToCharge
      })
      setIsLoading(false);
      if (response?.type === 'ok') {
        onContractCommitmentClose();
        onDeleteConfirmationModalOpen();
      } else {
        toast({
          title: "Couldn't charge customer",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (e) {
      console.error('Error charging customer for remaining months:', e);
    }
  }

  const contractCommitmentModal = (
    <Modal isOpen={isContractCommitmentOpen} onClose={onContractCommitmentClose} size={'lg'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Contract Commitment</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack spacing={4}>
            <Text>
              How many full months are left on
              <Text as={"span"} fontWeight={"bold"}> {' ' + businessToDel.name}</Text>
              {'\''}s contract after the current billing period that they{'\''}re required to pay?
            </Text>
            <Text fontStyle={"italic"} alignSelf={"flex-start"}>
              All remaining months will be charged immediately. The current billing period{'\''}s usage and metered
              plan fees are invoiced separately when the subscription is cancelled.
            </Text>
            <NumberInput
              mt={5}
              min={0}
              maxW={'50px'}
              size={"sm"}
              value={monthsToCharge}
              onChange={(_, valueAsNumber) => {
                setMonthsToCharge(Number.isNaN(valueAsNumber) ? 0 : Math.max(0, valueAsNumber))
              }}
              >
              <NumberInputField
                pr={2}
                pl={2}
                textAlign={"center"}
                inputMode={"numeric"}
                pattern={"[0-9]*"}/>
              <NumberInputStepper display="none" />
            </NumberInput>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button mr={3} variant={'ghost'} onClick={onDeleteRequirementsClose} isDisabled={isLoading}>Cancel</Button>
          <Button mr={3} onClick={() => {
            onDeleteRequirementsClose();
            chargeCustomerForRemainingMonths();
          }} isDisabled={isLoading}>Next</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const deleteSuccessModal = (
    <Modal isOpen={isDelSuccessOpen} onClose={onDelSuccessClose} size={'md'}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Success</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack spacing={4}>
            <Text>
              <Text as={"span"} fontWeight={"bold"}>{businessToDel.name}</Text>
              {'\''}s data deletion is scheduled.
              {!businessToDel.partner&&<Text fontStyle={"italic"} alignSelf={"flex-start"}>All remaining months will be charged immediately</Text>}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button onClick={() => {
            onDelSuccessClose();
            fetchAllCustomers();
          }}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const deleteBusinessFlow = async (business) => {
    setBusinessToDel(business);
    // first we check if everything is deleted
    const canDelete = await checkDeletionReqs(business.bid)
    if (canDelete) {
      if (business.partner) {
        onDeleteConfirmationModalOpen();
      } else {
        // For non partner businesses, we need to charge for remaining months
        onContractCommitmentOpen();
      }
    } else {
      // We can't delete yet: phone or PMS exist. Open delete requirements modal
      onDeleteRequirementsOpen();
    }
  }

  // Fetch bundles if partner
  const fetchBundles = async () => {
    const response = await cqAPI.get(
      "/settings/subscriptions/bundles"
    );
    if (response?.type === "ok") setBundles(response.data.bundles);
  };

  useEffect(() => {
    fetchAllCustomers();
    if (isPartner) fetchBundles();
  }, []);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const updateBusinessProfile = async (
    customer_id,
    field,
    value
  ) => {
    const response = await cqAPI.put(
      `/settings/business?bid=${customer_id}`,
      {
        field: "groupname",
        value,
      }
    );
    if (response.type === "ok") {
      setCustomers(
        customers.map((c) =>
          c.bid === customer_id
            ? { ...c, groupname: value }
            : c
        )
      );
      return true;
    }
    return false;
  };

  // Helper to format dates
  const fmt = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-US")
      : "";

  /**
   * The NewBusinessModal definition.
   */
  function NewBusinessModal({
    isOpen,
    isMapsLoaded,
    onClose,
    autocompleteRef,
    customers,
    bundles,
  }) {
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState("");
    const [selectedPlan, setSelectedPlan] = useState("None");
    const [activationDate, setActivationDate] = useState("");

    const options = { types: ["establishment"] };

    const handleGroupSelect = (e) =>
      setSelectedGroup(e.target.value);

    const handlePlaceSelect = () => {
      const place =
        autocompleteRef.current.getPlace();
      const isDentalOffice =
        place.types &&
        (place.types.includes("dentist") ||
          place.types.includes("doctor"));
      if (!isDentalOffice) {
        toast({
          title: "Invalid selection.",
          description:
            "You can only select a dental office.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }
      setSelectedPlace({
        name: place.name,
        address: place.formatted_address,
        location: place.geometry.location,
        place,
      });
    };

    const handleBack = () => setSelectedPlace(null);

    const confirmSelection = async () => {
      if (
        isPartner &&
        (selectedPlan === "None" ||
          !activationDate)
      ) {
        toast({
          title: "Missing required fields",
          description:
            "Plan and Activation Date cannot be empty.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }
      onClose();
      const requestBody = {
        place: selectedPlace.place,
        ...(selectedGroup
          ? { parent: selectedGroup }
          : {}),
        ...(isPartner
          ? {
              start_date: activationDate,
              bundle_id: selectedPlan,
            }
          : {}),
      };
      const response = await cqAPI.post(
        "/settings/business",
        requestBody
      );
      if (response.type === "ok")
        fetchAllCustomers();
      else if (response.msg) {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Select a Dental Office
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isMapsLoaded && selectedPlace ? (
              <>
                <Text fontWeight="bold">
                  {selectedPlace.name}
                </Text>
                <Text mb={4}>
                  {selectedPlace.address}
                </Text>
                <Box boxSize="sm">
                  <Image
                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${selectedPlace.location.lat()},${selectedPlace.location.lng()}&zoom=15&size=400x300&markers=|${selectedPlace.location.lat()},${selectedPlace.location.lng()}&key=${process.env.REACT_APP_MAP_API}`}
                    alt="Selected Place"
                  />
                </Box>
                <Button
                  colorScheme="blue"
                  mt={4}
                  onClick={confirmSelection}
                >
                  Confirm Selection
                </Button>
                <Button
                  colorScheme="gray"
                  mt={4}
                  ml={3}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </>
            ) : (
              <>
                <FormControl
                  id="group-select"
                  mt={4}
                >
                  <FormLabel>
                    Select a Group
                  </FormLabel>
                  <Select
                    onChange={handleGroupSelect}
                    placeholder="New Group"
                  >
                    <option value="">
                      New Group
                    </option>
                    {customers.map((c) => (
                      <option
                        key={c.bid}
                        value={c.bid}
                      >
                        {c.groupname || c.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                {isPartner && (
                  <>
                    <FormControl
                      id="plan-select"
                      mt={4}
                      isRequired
                    >
                      <FormLabel>
                        Plan
                      </FormLabel>
                      <Select
                        value={
                          selectedPlan
                        }
                        onChange={(e) =>
                          setSelectedPlan(
                            e.target.value
                          )
                        }
                      >
                        <option value="None">
                          None
                        </option>
                        {bundles.map((b) => (
                          <option
                            key={b._id}
                            value={b._id}
                          >
                            {b.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      id="activation-date"
                      mt={4}
                      isRequired
                    >
                      <FormLabel>
                        Activation Date
                      </FormLabel>
                      <Input
                        type="date"
                        value={
                          activationDate
                        }
                        onChange={(e) =>
                          setActivationDate(
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                  </>
                )}
                <FormControl
                  id="google-maps-autocomplete"
                  pt={4}
                >
                  <FormLabel>
                    Dental Office
                  </FormLabel>
                  {googleMap.isLoaded && (
                    <Autocomplete
                      options={options}
                      onPlaceChanged={
                        handlePlaceSelect
                      }
                      onLoad={(auto) =>
                        (autocompleteRef.current =
                          auto)
                      }
                    >
                      <Input
                        type="text"
                        placeholder="Type to search..."
                      />
                    </Autocomplete>
                  )}
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {!selectedPlace && (
              <Button
                colorScheme="blue"
                mr={3}
                onClick={onClose}
              >
                Close
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  const bgColor = useColorModeValue(
    theme.colors.lightBackground,
    theme.colors.darkBackground
  );
  const textColor = useColorModeValue(
    theme.colors.lightText,
    theme.colors.darkText
  );
  const secondaryTextColor = useColorModeValue(
    theme.colors.gray[600],
    theme.colors.gray[400]
  );
  const searchBgColor = useColorModeValue(
    "#f0f4f8",
    "#2d3748"
  );
  const mobileMarginLeft = useBreakpointValue({
    base: "20px",
    md: "0",
  });
  const searchHoverBgColor = useColorModeValue(
    theme.colors.gray[200],
    theme.colors.gray[700]
  );
  const searchResultBgColor = useColorModeValue(
    theme.colors.lightBackground,
    "#0f1a25"
  );

  const formatLocationOption = (option) => (
    <Flex direction="column" py={1}>
      {/* first line: dot + name */}
      <Flex align="center">
        <Box
          as="span"
          w={2}
          h={2}
          borderRadius="full"
          bg={option.start_date ? "green.500" : "gray.500"}
          mr={2}
        />
        <Text>{option.name}</Text>
      </Flex>
      {/* second line: dates */}
      <Text fontSize="sm" color="gray.500" pl={4}>
        Created: {fmt(option.created)}
        {option.start_date
          ? `  ·  Activated: ${fmt(option.start_date)}`
          : ""}
      </Text>
    </Flex>
  );
  

  const DropdownIndicator = (props) =>
    components.DropdownIndicator ? (
      <components.DropdownIndicator {...props}>
        <FontAwesomeIcon icon={faSearch} />
      </components.DropdownIndicator>
    ) : null;
  const _loadSuggestions = (query, cb) =>
    loadCustomers(query).then(cb);
  const debouncedLoadCustomers = debounce(
    _loadSuggestions,
    1000
  );
  const handleSelectChange = (opt) => {
    const bid = opt?._id?.toString();
    if (bid) navigate(`/?bid=${bid}`);
  };
  
  const loadOptions = useCallback(
      debounce((inputValue, callback) => {
        loadCustomers(inputValue).then((options) => callback(options));
      }, 500),
      []
    );

    /* cancel debounce on unmount to prevent state updates after unmount */
    useEffect(() => {
      return () => loadOptions.cancel();
    }, [loadOptions]);

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
    >
      {deleteRequirementsModal}
      {contractCommitmentModal}
      {deleteConfirmationModal}
      {deleteSuccessModal}
      {stopDeletionModal}
      <NewBusinessModal
        isOpen={isNewBusinessOpen}
        isMapsLoaded={googleMap.isLoaded}
        onClose={onNewBusinessClose}
        autocompleteRef={autocompleteRef}
        customers={customers}
        bundles={bundles}
      />
      <Flex
        gap={8}
        mt={2}
        ml={2}
        mr={4}
        mb={4}
      >
        <Box
          width="100%"
          id="location-search-bar"
        >
      <AsyncSelect
        cacheOptions
        defaultOptions
        loadOptions={loadOptions}
        formatOptionLabel={formatLocationOption}
        getOptionValue={(opt) => opt._id}
        placeholder="Search Locations"
        components={{ DropdownIndicator }}
        className="search-input"
        onChange={handleSelectChange}
      />
        </Box>
        <Spacer />
        {isAdecPartner && (
          <Menu>
          <MenuButton as={Button} minWidth="150px" mr={2} rightIcon={<ChevronDownIcon />}>
          Export
          </MenuButton>
          <MenuList>
          <MenuItem onClick={() => exportCustomers('csv')}>Export CSV</MenuItem>
          <MenuItem onClick={() => exportCustomers('pdf')}>Export PDF</MenuItem>
          </MenuList>
          </Menu>
        )}
        {Auth.user?.isSuperAdmin && (
          <Button
            minWidth="150px"
            leftIcon={<AddIcon />}
            onClick={
              onNewBusinessOpen
            }
          >
            Add Location
          </Button>
        )}
      </Flex>
      <Flex
        p={2}
        bg={useColorModeValue(
          "gray.100",
          "gray.900"
        )}
        fontWeight="semibold"
      >
        <HStack spacing={4} flex="1">
          <Box w="40px" flexShrink={0} />
          <Box w={isAdecPartner ? "180px" : "40%"} flexShrink={0}>
            Business
        </Box>
        {isAdecPartner && (
          <>
          <Box w="130px" flexShrink={0}>Subscription Part #</Box>
          <Box w="120px" flexShrink={0}>TM Name</Box>
          <Box w="80px" flexShrink={0}>Zipcode</Box>
          </>
        )}
        <Box flex="1">
        
            Subscription Start
        </Box>
        <Box flex="1">
            Account Creating Date
        </Box>
        <Box w="40px" flexShrink={0} />
        <Box w="40px" flexShrink={0} />
        <Box w="40px" flexShrink={0} />
        </HStack>
      </Flex>
      <VStack
        spacing={4}
        align="stretch"
      >
        {customers.map((c) => (
          <Box key={c.bid} className="customer-entry">
            <Flex
              p={2}
              _hover={{
                bg: hoverBgColor,
                cursor: "pointer",
              }}
                onClick={() => navigate(`/?bid=${c.bid}`)}
            >
              <HStack
                spacing={8}
                justifyItems={"stretch"}
                flex="1"
                align="center"
                color = {c.deleted ? "red" : textColor}
              >
                <Box w="40px" flexShrink={0}>
                  {c.locations?.length > 1 && (
                    <IconButton
                      icon={
                        expandedRows[c.bid] ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronRightIcon />
                        )
                      }
                      aria-label="Expand"
                      id={`expand-${c.bid}`}
                      onClick={(e) =>{
                        e.stopPropagation();
                        toggleExpand(c.bid)
                      }
                      }
                    />
                  )}
                </Box>
                <Flex w={isAdecPartner ? "180px" : "40%"} flexShrink={0} align="center" onClick={(e) => e.stopPropagation()}>
                  <EditableText
                    initialValue={
                      c.groupname || c.name
                    }
                    placeholder="Group Name"
                    pattern={/[a-zA-Z]+/}
                    invalid="Cannot be empty"
                    onSave={(v) =>
                      updateBusinessProfile(
                        c.bid,
                        "groupname",
                        v
                      )
                    }
                    textClicked={()=>navigate(`/?bid=${c.bid}`)}
                    editable={
                      Auth.user?.isSuperAdmin
                    }
                  />
                </Flex>
                    {isAdecPartner && (
                      <>
                      <Flex w="130px" flexShrink={0} align="center" onClick={(e) => e.stopPropagation()}>
                      <Select
                      size="sm"
                      value={c.subscription_part_number || ''}
                      onChange={(e) => updatePartnerData(c.bid, 'subscription_part_number', e.target.value)}
                      placeholder="Select"
                      >
                      {SUBSCRIPTION_PART_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                      </Select>
                      </Flex>
                      <Flex w="120px" flexShrink={0} align="center" onClick={(e) => e.stopPropagation()}>
                      <EditableText
                      initialValue={c.tm_name || ''}
                      placeholder="TM Name"
                      onSave={(v) => updatePartnerData(c.bid, 'tm_name', v)}
                      editable={true}
                      />
                      </Flex>
                      <Flex w="80px" flexShrink={0} align="center">
                      <Text>{c.zipcode || ''}</Text>
                      </Flex>
                      </>
                    )}
                    <Box flex="1" display="flex" alignItems="center">

                  <Popover trigger="hover">
                    <PopoverTrigger>
                      <Box
                        as="span"
                        w={3}
                        h={3}
                        borderRadius="full"
                        bg={
                          c.start_date
                            ? "green.500"
                            : "gray.500"
                        }
                        mr={2}
                        cursor="pointer"
                      />
                    </PopoverTrigger>
                    <PopoverContent>
                      <PopoverArrow />
                      <PopoverBody fontSize="sm">
                        Green = active account
                        <br />
                        Gray = never activated
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                  <Text>
                    {fmt(c.start_date)}
                  </Text>
                    </Box>
                    <Text flex="1">
                  {fmt(c.created)}
                </Text>
                <Box w="40px" flexShrink={0}>
                  {c.locations?.length}
                  </Box>
                  <Box
                  w="40px"
                  flexShrink={0}
                  display="flex"
                  justifyContent="flex-end"
                  className="navigate-to-business"
                >
                  <ChevronRightIcon />
                  </Box>
                  <Box w="40px" flexShrink={0} onClick={(e) => e.stopPropagation()}>
                  {!c.deleted && Auth.user?.isSuperAdmin &&
                    (c.scheduled_for_deletion
                    ?
                      <Flex
                        justifyContent={"center"}
                        onClick={() => {
                          setBusinessToDel(c);
                          onStopDeletionModalOpen();
                        }
                        }
                      >
                        <RiResetLeftFill />
                      </Flex>
                    : <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FontAwesomeIcon icon={faEllipsisV}/>}
                        variant="ghost"
                      />
                      <MenuList>
                        <MenuItem
                          key={`Delete-${c.bid}`}
                          onClick={() => deleteBusinessFlow(c)}
                          color={"red"}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Box>
              </HStack>
            </Flex>
            {expandedRows[c.bid] && (
              <VStack
                align="stretch"
                pl={12}
                mt={2}
                ml={16}
                justifySelf={"flex-start"}
                alignItems={"flex-start"}
              >
                {c.locations.map((loc) => (
                  <Link
                    key={loc._id}
                    fontSize="sm"
                    id={`location-link-${loc._id}`}
                    onClick={() =>
                      navigate(`/?bid=${loc._id}`)
                    }
                  >
                    {loc.name}
                  </Link>
                ))}
              </VStack>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

export default AllCustomers;